// src/services/adjustmentDerivation.js
//
// Adjustment status resolution (PRD_StandaloneAdjustments C3). The consumption
// LINK is persisted (`consumedRunId` on the adjustment doc, written only
// server-side by savePayrollRun/deletePayrollRun); the STATUS is derived by
// looking that run id up in the payroll index (companies/{cid}/payrollIndex/
// master.runs[], read via loadActiveLocks — one single-doc read serves a page):
//
//   consumedRunId == null                          → pending      (editable)
//   run found, status 'draft'                      → in-draft     (editable, banner)
//   run found, 'pending-approval' / 'completed'    → paid         (frozen)
//   run absent, or an unknown status (e.g. a future
//   'archived' housekeeping state)                 → link-broken  (frozen, FAIL-CLOSED)
//
// Link-broken is never Pending: the index entry is written client-side AFTER
// savePayrollRun returns and its failure is only toasted, so "link set + run
// absent" is a failure state, not a deletion — resolving it Pending would make
// an entry editable while it sits embedded in a run about to pay. Legitimate
// deletion clears the link first (C21/C25), so this row only means "something
// went wrong"; recovery is a one-off script, deliberately no UI action.
//
// There is deliberately NO period-coverage test here — coverage cannot
// distinguish a run that covered an employee+date from one that actually took
// the entry, so a completed run could mask the draft run that holds the link.
// (That is also why deriveLockStatus is NOT reused: its precedence walk keys on
// coverage.) Mirrored in functions/src/payroll/adjustmentStatus.js (C22) —
// both suites assert the shared golden fixture
// src/test-fixtures/adjustmentStatusScenarios.json; keep the twins in sync.

// Status keys (stable identifiers; display labels live in the page component).
export const ADJ_STATUS = {
  PENDING: 'pending',
  IN_DRAFT: 'in-draft',
  PAID: 'paid',
  LINK_BROKEN: 'link-broken',
};

// The only run statuses the resolution recognizes (PRD_PayrollApproval lifecycle).
const RUN_STATUS = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending-approval',
  COMPLETED: 'completed',
};

/**
 * Resolve one adjustment's status from its consumption link + the index runs.
 *
 * Pure — no I/O. Callers load `runs` once via loadActiveLocks(companyId)
 * (src/services/dtrLockDerivation.js) and map over their rows.
 *
 * @param {string|null|undefined} consumedRunId - the adjustment's persisted link
 * @param {Array} runs - index run entries ({ runId, status, ... })
 * @returns {{ status: string, runId: string|null, editable: boolean }}
 */
export function resolveAdjustmentStatus(consumedRunId, runs) {
  if (consumedRunId == null) {
    return { status: ADJ_STATUS.PENDING, runId: null, editable: true };
  }
  const run = (Array.isArray(runs) ? runs : []).find((r) => r && r.runId === consumedRunId);
  if (!run) {
    return { status: ADJ_STATUS.LINK_BROKEN, runId: consumedRunId, editable: false };
  }
  if (run.status === RUN_STATUS.DRAFT) {
    return { status: ADJ_STATUS.IN_DRAFT, runId: consumedRunId, editable: true };
  }
  if (run.status === RUN_STATUS.PENDING_APPROVAL || run.status === RUN_STATUS.COMPLETED) {
    return { status: ADJ_STATUS.PAID, runId: consumedRunId, editable: false };
  }
  // Unknown status (e.g. a future 'archived' housekeeping state) — fail closed:
  // never editable, never re-payable (C3's fourth row).
  return { status: ADJ_STATUS.LINK_BROKEN, runId: consumedRunId, editable: false };
}

/**
 * True when the resolved status means the entry is unpaid — i.e. it should
 * appear in the page's always-visible section (C13/C16): never claimed, or
 * claimed by a run that has not yet paid (an abandoned draft, a rejected run).
 * Link-broken is deliberately NOT unpaid — fail-closed, frozen.
 *
 * @param {string} status - an ADJ_STATUS value
 * @returns {boolean}
 */
export function isUnpaidStatus(status) {
  return status === ADJ_STATUS.PENDING || status === ADJ_STATUS.IN_DRAFT;
}
